import type { LocalCartItem } from '@/types';
import {
  clearCartMergePending,
  mergeCartWithNotification,
  setCartMergePending,
} from '@/utils/mergeCartWithNotification';
import * as Sentry from '@sentry/nextjs';
import { signIn } from 'next-auth/react';

// マージ API は冪等。短い指数バックオフで自動リトライする
const AUTO_RETRY_DELAYS_MS = [500, 1500] as const;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `mergeCartWithNotification` を指数バックオフで自動リトライするラッパ。
 * 初回 + AUTO_RETRY_DELAYS_MS.length 回のリトライを試行し、全失敗時は最後のエラーを throw する。
 *
 * 戻り値 `cartMerged`: マージで既存サーバカートの件数が増えたか（呼び出し側が /cart 誘導の判定に使う）
 */
const mergeWithRetry = async (items: LocalCartItem[], clearCartInfo: () => void): Promise<boolean> => {
  for (let attempt = 0; attempt <= AUTO_RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await mergeCartWithNotification(items, clearCartInfo);
    } catch (error) {
      if (attempt === AUTO_RETRY_DELAYS_MS.length) throw error;
      await wait(AUTO_RETRY_DELAYS_MS[attempt]);
    }
  }
  // ループは return か throw で必ず抜けるため到達しない。TS の戻り値推論用
  throw new Error('mergeWithRetry: unreachable');
};

type SignInAndMergeOutcome = 'noMerge' | 'cartMerged' | 'mergeFailed';

type SignInAndMergeResult = { ok: true; outcome: SignInAndMergeOutcome } | { ok: false; error?: string };

type SignInAndMergeDeps = {
  getItems: () => LocalCartItem[];
  clearCartInfo: () => void;
};

export type SignInAndMergeArgs = {
  token: string;
};

/**
 * `signIn('with-token', ...)` と ローカルカートのサーバマージ を行う。
 * 遷移は行わない（呼び出し側の責務）。
 *
 * 戻り値:
 *   - `ok: false`               ログイン自体が失敗
 *   - `outcome: 'noMerge'`      ローカルカート空 or マージしたが既存サーバカート件数が増えていない（callbackUrl に遷移可）
 *   - `outcome: 'cartMerged'`   マージで既存サーバカートに件数が積み増しされた（呼び出し側は /cart 誘導推奨）
 *   - `outcome: 'mergeFailed'`  自動リトライ全敗（ローカルカートは未消費、Sentry に通知済み）
 *
 * - マージ API は冪等なので HTTP 層で 3 回まで自動リトライする
 * - 失敗した場合はサイレントに mergeFailed を返す。ユーザにダイアログは出さず /cart 誘導でカート状態を確認させる
 * - `mergeCartWithNotification` は成功時に sessionStorage にバナー通知フラグを立てる（/cart 画面で消費）
 */
export const signInAndMerge = async (
  deps: SignInAndMergeDeps,
  args: SignInAndMergeArgs,
): Promise<SignInAndMergeResult> => {
  const signInResult = await signIn('with-token', { token: args.token, redirect: false });
  if (!signInResult?.ok) {
    return { ok: false, error: signInResult?.error ?? undefined };
  }

  const items = deps.getItems();
  if (items.length === 0) {
    clearCartMergePending();
    return { ok: true, outcome: 'noMerge' };
  }

  try {
    const cartMerged = await mergeWithRetry(items, deps.clearCartInfo);
    return { ok: true, outcome: cartMerged ? 'cartMerged' : 'noMerge' };
  } catch (error) {
    Sentry.captureException(error);
    // ローカルカートは未消費。GfCheckCart が次回ページアクセス時にこのフラグを見て再試行する
    setCartMergePending();
    return { ok: true, outcome: 'mergeFailed' };
  }
};
